import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, Users, LayoutGrid, List, Clock, CheckCircle2, AlertTriangle, Coffee } from 'lucide-react';

// ── Team Roster (sorter portal) ───────────────────────────────────────────────
// Read-only window onto the published week: every position, every night, and who is
// on it. Same data the admin roster grid publishes (sorter_slots + sorter_roster),
// and the SAME visual language — area group boxes with their colour cap, and each
// person in their own member colour / preferred name — so a sorter reading this and a
// manager reading /sorters-admin are looking at the same picture.
//
// Read-only by design: nothing here writes. "Can't work" stays on the My Shifts tab.

const API = '/.netlify/functions/appsscript';
function call(action, payload) {
  return fetch(API, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...(payload || {}) }),
  }).then(r => r.json());
}

// NEVER toISOString() for calendar dates — it converts to UTC and knocks Sydney dates
// back a day (the same trap the admin page documents).
const isoLocal = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function sundayOf(iso) { const d = new Date(iso + 'T00:00'); d.setDate(d.getDate() - d.getDay()); return isoLocal(d); }
function weekDates(weekStart) { const d = new Date(weekStart + 'T00:00'); return Array.from({ length: 7 }, (_, i) => { const x = new Date(d); x.setDate(d.getDate() + i); return isoLocal(x); }); }
const fmtD = (iso) => new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
// The "current" shift night: overnight shifts end in the morning, so before midday the
// night on everyone's mind is yesterday's (same rule as the finish-time banner).
function currentShiftDate() { const d = new Date(); if (d.getHours() < 12) d.setDate(d.getDate() - 1); return isoLocal(d); }

// A night operates if its weekday is in cfg.days, OR it sits inside the peak window and
// its weekday is in cfg.peakDays (e.g. Friday through pre-Xmas peak).
function isOperatingDay(dateISO, cfg) {
  if (!cfg || !Array.isArray(cfg.days)) return true;
  const dow = new Date(dateISO + 'T00:00').getDay();
  if (cfg.days.includes(dow)) return true;
  if (cfg.peakFrom && cfg.peakTo && dateISO >= cfg.peakFrom && dateISO <= cfg.peakTo && (cfg.peakDays || []).includes(dow)) return true;
  return false;
}

// Area grouping + colours — MIRRORS slotGroup() in SortersAdminPage.jsx. Keep the two in
// step: the whole point is that the sorter's roster reads the same as the manager's.
// Order matters: "Sorter City 4 – Used when TL is Runner" is a sorter (city), so the city
// test runs before the lead check.
function slotGroup(sl) {
  const cat = (sl && sl.Category) || '', n = (sl && sl.Slot) || '';
  if (/broadway/i.test(n)) return { key: 'broadway', label: 'Broadway', colour: '#F97316' };
  if (/darlo|darlinghurst/i.test(n)) return { key: 'darlo', label: 'Darlinghurst', colour: '#14B8A6' };
  if (/xmas|christmas/i.test(n)) return { key: 'xmas', label: 'Xmas Casual', colour: '#E11D48' };
  if (/city/i.test(n)) return { key: 'city', label: 'City', colour: '#3B82F6' };
  if (cat === 'lead') return { key: 'lead', label: 'Team Leader & Runners', colour: '#A855F7' };
  return { key: 'other', label: 'Other', colour: '#6B7280' };
}
// The legend, in the admin page's order (areas as the manager lists them).
const AREA_LEGEND = [
  { key: 'lead', label: 'Team Leader', colour: '#A855F7' },
  { key: 'city', label: 'City', colour: '#3B82F6' },
  { key: 'darlo', label: 'Darlinghurst', colour: '#14B8A6' },
  { key: 'broadway', label: 'Broadway', colour: '#F97316' },
  { key: 'xmas', label: 'Xmas additional', colour: '#E11D48' },
];

// Clock helpers — same shapes the Clock tab uses (a 30-min unpaid break per shift).
const BREAK_MIN = 30;
function fmtTime(iso) { if (!iso) return '—'; try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } }
function fmtDur(ms) { if (ms < 0) ms = 0; const s = Math.floor(ms / 1000); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60; return (h ? h + ':' : '') + String(m).padStart(2, '0') + ':' + String(ss).padStart(2, '0'); }

// "Jose Luis Astorga" → "Jose A." — the same shortening the admin grid tiles use, so a
// long name never blows the cell out. A preferred name (nick) always wins.
const shortName = (name, login) => {
  const parts = (name || '').replace(/\(.*?\)/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!parts[0]) return login || '—';
  return parts.length > 1 ? parts[0] + ' ' + parts[parts.length - 1][0] + '.' : parts[0];
};

const S = {
  card: { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg, 14px)', padding: '18px 16px', marginBottom: 16 },
  title: { fontFamily: 'var(--font-display)', fontSize: '1.15rem', letterSpacing: '0.03em', margin: '0 0 4px' },
  sub: { fontSize: '0.8rem', color: 'var(--hp-dim)', margin: '0 0 14px', lineHeight: 1.5 },
  ghost: { padding: '7px 10px', background: 'transparent', color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.76rem', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)' },
  pill: (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.7rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: bg, color: fg }),
};

export default function SorterRosterTab({ sorter }) {
  const me = (sorter && sorter.login || '').toUpperCase();
  const [weekStart, setWeekStart] = useState(() => sundayOf(currentShiftDate()));
  const [slots, setSlots] = useState([]);
  const [grid, setGrid] = useState({});          // `date|slot` → {login,name,start,end}
  const [colours, setColours] = useState({});    // LOGIN → {colour, nick, gender}
  const [operating, setOperating] = useState({ days: [0, 1, 2, 3, 4], peakDays: [5], peakFrom: '', peakTo: '' });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('night');     // 'night' = one night's positions · 'week' = the whole grid
  const [pick, setPick] = useState(() => currentShiftDate());

  const days = useMemo(() => weekDates(weekStart), [weekStart]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ds = weekDates(weekStart);
      const [s, r, mc, op] = await Promise.all([
        call('getSorterSlots', {}),
        call('getSorterRoster', { from: ds[0], to: ds[6] }),   // whole week, everyone — no login filter
        call('getMemberColours', {}),
        call('getOperatingDays', {}),
      ]);
      setSlots(s && s.success ? (s.slots || []) : []);
      const g = {};
      ((r && r.roster) || []).forEach(row => {
        g[row.Date + '|' + row.Slot] = { login: (row.Login || '').toUpperCase(), name: row.Name || '', start: row.Start || '', end: row.End || '' };
      });
      setGrid(g);
      setColours((mc && mc.success && mc.colours) ? mc.colours : {});
      if (op && op.success && op.operating) setOperating(op.operating);
    } catch (e) { setSlots([]); setGrid({}); }
    finally { setLoading(false); }
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  // Keep the picked night inside the week on screen, and land on a night that actually
  // operates (Fri/Sat outside peak have no shift at all).
  useEffect(() => {
    if (days.includes(pick) && isOperatingDay(pick, operating)) return;
    const inWeek = days.includes(pick) ? pick : (days.includes(currentShiftDate()) ? currentShiftDate() : days[0]);
    if (isOperatingDay(inWeek, operating)) { setPick(inWeek); return; }
    setPick(days.find(d => isOperatingDay(d, operating)) || days[0]);
  }, [days, operating, pick]);

  const colourOf = (login) => (colours[(login || '').toUpperCase()] || {}).colour || '';
  const nickOf = (login) => (colours[(login || '').toUpperCase()] || {}).nick || '';
  const nameOf = (login, fullName) => nickOf(login) || shortName(fullName, login);

  const shiftWeek = (n) => { const d = new Date(weekStart + 'T00:00'); d.setDate(d.getDate() + n * 7); setWeekStart(isoLocal(d)); };

  // Slots in their published order, cut into area blocks — one box per area, exactly
  // as the admin grid boxes them.
  const groups = useMemo(() => {
    const out = [];
    slots.forEach(sl => {
      const g = slotGroup(sl);
      const last = out[out.length - 1];
      if (last && last.key === g.key) last.slots.push(sl);
      else out.push({ ...g, slots: [sl] });
    });
    return out;
  }, [slots]);

  const filledOn = (date) => slots.reduce((n, sl) => n + ((grid[date + '|' + sl.Slot] || {}).login ? 1 : 0), 0);
  const myShiftOn = (date) => slots.map(sl => grid[date + '|' + sl.Slot]).find(a => a && a.login === me);

  const thisWeek = sundayOf(currentShiftDate()) === weekStart;

  // Tonight's own row on the roster — only known when the week on screen contains it (the
  // card still shows the clock + finish time when you're browsing another week).
  const tonight = currentShiftDate();
  const myTonight = useMemo(() => {
    if (!days.includes(tonight)) return undefined;   // browsing another week — say nothing
    const sl = slots.find(s => ((grid[tonight + '|' + s.Slot] || {}).login === me));
    if (!sl) return null;                            // in this week, and not on tonight
    const a = grid[tonight + '|' + sl.Slot];
    return { slot: sl.Slot, start: a.start || sl['Default Start'] || '', end: a.end || sl['Default End'] || '' };
  }, [days, slots, grid, me, tonight]);

  return (
    <>
      <TonightCard sorter={sorter} myTonight={myTonight} operates={isOperatingDay(tonight, operating)} />

      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Users size={18} color="var(--hp-gold)" />
          <h2 style={{ ...S.title, margin: 0, flex: 1 }}>Team roster</h2>
          <button style={S.ghost} onClick={() => setView(v => (v === 'night' ? 'week' : 'night'))}>
            {view === 'night' ? <><LayoutGrid size={13} /> Whole week</> : <><List size={13} /> One night</>}
          </button>
        </div>
        <p style={{ ...S.sub, marginTop: 8 }}>Who is on each position, night by night. This is the published roster — if something looks wrong, tell your team leader or the office.</p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <button style={{ ...S.ghost, padding: '7px 9px' }} onClick={() => shiftWeek(-1)}><ChevronLeft size={14} /></button>
          <div style={{ flex: 1, textAlign: 'center', fontWeight: 800, fontSize: '0.84rem' }}>
            {fmtD(days[0])} → {fmtD(days[6])}
            <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', fontWeight: 600 }}>{thisWeek ? 'This week' : days[6].slice(0, 4)}</div>
          </div>
          <button style={{ ...S.ghost, padding: '7px 9px' }} onClick={() => shiftWeek(1)}><ChevronRight size={14} /></button>
        </div>

        {/* Night picker — greys the nights that don't operate, flags the ones you're on. */}
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          {days.map((d, i) => {
            const op = isOperatingDay(d, operating);
            const mine = !!myShiftOn(d);
            const active = view === 'night' && pick === d;
            return (
              <button key={d} disabled={!op} onClick={() => { setPick(d); setView('night'); }}
                title={op ? `${DOW[i]} ${fmtD(d)} — ${filledOn(d)} on` : 'No shift this night'}
                style={{
                  flex: '1 0 auto', minWidth: 62, padding: '7px 6px', borderRadius: 10, cursor: op ? 'pointer' : 'default',
                  background: active ? 'rgba(201,168,76,0.16)' : 'transparent',
                  border: `1px ${op ? 'solid' : 'dashed'} ${active ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                  color: active ? 'var(--hp-gold)' : op ? 'var(--hp-text)' : 'var(--hp-dim)',
                  opacity: op ? 1 : 0.5, fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.74rem', lineHeight: 1.35,
                }}>
                {DOW[i]}<br />
                <span style={{ fontWeight: 600, fontSize: '0.68rem', color: 'var(--hp-dim)' }}>{new Date(d + 'T00:00').getDate()}</span><br />
                <span style={{ fontWeight: 700, fontSize: '0.62rem', color: op ? (mine ? 'var(--hp-gold)' : 'var(--hp-dim)') : 'var(--hp-dim)' }}>
                  {op ? (mine ? '● you' : `${filledOn(d)} on`) : 'no shift'}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ ...S.card, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</div>
      ) : !slots.length ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '40px 20px' }}><CalendarDays size={28} color="var(--hp-dim)" /><p style={S.sub}>No positions set up yet.</p></div>
      ) : view === 'night' ? (
        <NightView groups={groups} date={pick} grid={grid} me={me} operating={operating} colourOf={colourOf} nameOf={nameOf} />
      ) : (
        <WeekView groups={groups} days={days} grid={grid} me={me} operating={operating} colourOf={colourOf} nameOf={nameOf} />
      )}

      <div style={{ ...S.card, marginBottom: 40 }}>
        <div style={{ fontSize: '0.66rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Areas</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {AREA_LEGEND.map(c => (
            <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.74rem', color: 'var(--hp-dim)' }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: c.colour, display: 'inline-block' }} />{c.label}
            </span>
          ))}
        </div>
        <p style={{ ...S.sub, margin: '12px 0 0' }}>Everyone has their own colour and preferred name — the same ones the office uses on the roster. Your own shifts are outlined in gold.</p>
      </div>
    </>
  );
}

// ── Tonight — my clock status + the finish time AP confirmed ─────────────────
// Both read-only here. Clocking on/off keeps its geofence + direction-locked QR rules and
// stays on the Clock tab — this card only answers "am I on the clock, and when do we
// finish?" so a sorter reading the roster doesn't have to hop tabs to find out.
function TonightCard({ sorter, myTonight, operates }) {
  const date = currentShiftDate();
  const [shift, setShift] = useState(undefined);   // undefined = loading · null = off the clock
  const [finish, setFinish] = useState(undefined); // undefined = loading · null = not confirmed yet
  const [, setNow] = useState(Date.now());

  const load = useCallback(() => {
    call('getSorterActiveShift', { login: sorter.login })
      // `stale` = an open row from a night that's already over (never clocked off). It is
      // NOT "on shift now" — running a timer on it is what hid Laura's lost shift.
      .then(r => setShift(r && r.success ? (r.stale ? { ...r.shift, stale: true } : r.shift) : null)).catch(() => setShift(null));
    call('nsGetFinishTime', { shiftDate: date })
      .then(r => setFinish(r && r.success ? (r.finish || null) : null)).catch(() => setFinish(null));
  }, [sorter.login, date]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  // Live timers only matter while someone is actually on the clock — a stale open row from
  // an earlier night is an unfinished shift to fix, not time being worked now.
  const staleShift = !!(shift && shift.stale);
  const onShift = !!(shift && shift['Clock In'] && !staleShift);
  useEffect(() => {
    if (!onShift) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [onShift]);

  const onBreak = onShift && shift['Break Start'] && !shift['Break End'];
  const breakDone = onShift && shift['Break Start'] && shift['Break End'];
  const remainingMs = onBreak ? (BREAK_MIN * 60 * 1000 - (Date.now() - new Date(shift['Break Start']).getTime())) : 0;
  const overdue = onBreak && remainingMs < 0;

  const tone = overdue ? { fg: 'var(--hp-red, #EF4444)', bg: 'rgba(239,68,68,0.12)' }
    : (onBreak || staleShift) ? { fg: 'var(--hp-amber, #F59E0B)', bg: 'rgba(245,158,11,0.14)' }
      : onShift ? { fg: 'var(--hp-green, #22C55E)', bg: 'rgba(34,197,94,0.12)' }
        : { fg: 'var(--hp-dim)', bg: 'rgba(148,163,184,0.12)' };

  return (
    <div style={{ ...S.card, borderColor: onShift ? tone.fg : 'var(--hp-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Clock size={17} color="var(--hp-gold)" />
        <h2 style={{ ...S.title, margin: 0, flex: 1 }}>Tonight</h2>
        <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', fontWeight: 700 }}>{new Date(date + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
      </div>

      {/* My clock status */}
      {shift === undefined ? (
        <div style={{ color: 'var(--hp-dim)', fontSize: '0.82rem' }}><Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> Checking the clock…</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: tone.bg, border: `1px solid ${(onShift || staleShift) ? tone.fg : 'var(--hp-border)'}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 800, color: tone.fg }}>
              {staleShift ? 'An earlier shift was never clocked off'
                : overdue ? 'Break overdue — clock back on' : onBreak ? 'On break' : onShift ? `On shift since ${fmtTime(shift['Clock In'])}` : 'Off the clock'}
            </div>
            <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 2 }}>
              {staleShift
                ? `Your ${shift.Date ? new Date(shift.Date + 'T00:00').toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' }) : 'last'} shift is still open — you're not on the clock now. Open the Clock tab to sort it out.`
                : onShift
                  ? (onBreak ? 'End your break from the Clock tab.'
                    : breakDone ? `30-min break taken (${fmtTime(shift['Break Start'])}–${fmtTime(shift['Break End'])})`
                      : 'Clock off from the Clock tab when you finish.')
                  : myTonight ? `You're on ${myTonight.slot} · ${myTonight.start}–${myTonight.end} — clock on from the Clock tab.`
                    : myTonight === null ? (operates ? "You're not rostered tonight." : 'No shift tonight.')
                      : 'Clock on from the Clock tab when you get to the depot.'}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono, monospace)', fontWeight: 800, fontSize: '1.15rem', color: tone.fg }}>
            {staleShift ? <AlertTriangle size={18} color={tone.fg} />
              : onBreak ? <>{overdue ? '-' : ''}{fmtDur(Math.abs(remainingMs))}<div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--hp-dim)', fontFamily: 'var(--font-body)' }}>{overdue ? 'OVER' : 'LEFT'}</div></>
                : onShift ? <>{fmtDur(Date.now() - new Date(shift['Clock In']).getTime())}<div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--hp-dim)', fontFamily: 'var(--font-body)' }}>ON SHIFT</div></>
                  : <Coffee size={18} color="var(--hp-dim)" />}
          </div>
        </div>
      )}

      {/* Shift finish time — AP's morning call, keyed in by the office */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, marginTop: 8, background: finish ? 'rgba(201,168,76,0.10)' : 'transparent', border: `1px solid ${finish ? 'var(--hp-gold)' : 'var(--hp-border)'}` }}>
        {finish === undefined ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite', color: 'var(--hp-dim)' }} /> : finish ? <CheckCircle2 size={16} color="var(--hp-gold)" /> : <AlertTriangle size={16} color="var(--hp-dim)" />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 800, color: finish ? 'var(--hp-gold)' : 'var(--hp-dim)' }}>
            {finish === undefined ? 'Finish time…' : finish ? `Finish time ${finish.finishTime}` : 'Finish time not confirmed yet'}
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 2 }}>
            {finish ? `Confirmed by Australia Post${finish.note ? ' · ' + finish.note : ''}` : finish === null ? 'Australia Post send it through in the morning — it shows here as soon as the office keys it in.' : ''}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── One night — area boxes, a row per position ───────────────────────────────
function NightView({ groups, date, grid, me, operating, colourOf, nameOf }) {
  if (!isOperatingDay(date, operating)) {
    return <div style={{ ...S.card, textAlign: 'center', padding: '40px 20px' }}><CalendarDays size={28} color="var(--hp-dim)" /><p style={S.sub}>No shift this night.</p></div>;
  }
  const d = new Date(date + 'T00:00');
  const on = groups.reduce((n, g) => n + g.slots.filter(sl => (grid[date + '|' + sl.Slot] || {}).login).length, 0);
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <h2 style={{ ...S.title, margin: 0 }}>{d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}</h2>
        <span style={S.pill('rgba(201,168,76,0.14)', 'var(--hp-gold)')}>{on} on tonight</span>
      </div>

      {groups.map(g => (
        <div key={g.key} style={{ border: `1px solid ${g.colour}55`, borderLeft: `4px solid ${g.colour}`, borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ padding: '6px 12px', background: `${g.colour}14`, borderBottom: `1px solid ${g.colour}33`, color: g.colour, fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{g.label}</div>
          {g.slots.map(sl => {
            const a = grid[date + '|' + sl.Slot];
            const mine = a && a.login === me;
            const col = a && a.login ? colourOf(a.login) : '';
            const start = (a && a.start) || sl['Default Start'] || '';
            const end = (a && a.end) || sl['Default End'] || '';
            return (
              <div key={sl.Slot} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderTop: `1px solid var(--hp-border)`, background: mine ? 'rgba(201,168,76,0.08)' : 'transparent' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sl.Slot}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>{start}–{end}</div>
                </div>
                {a && a.login ? (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '52%', padding: '5px 10px', borderRadius: 999,
                    background: col ? col + '2b' : 'rgba(201,168,76,0.12)',
                    border: `1px solid ${mine ? 'var(--hp-gold)' : (col || 'var(--hp-border)')}`,
                    fontSize: '0.78rem', fontWeight: 700,
                  }}>
                    {col && <span style={{ width: 8, height: 8, borderRadius: '50%', background: col, flex: '0 0 auto' }} />}
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameOf(a.login, a.name)}</span>
                    {mine && <span style={{ color: 'var(--hp-gold)', fontSize: '0.66rem', fontWeight: 800 }}>YOU</span>}
                  </span>
                ) : (
                  <span style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', fontWeight: 600 }}>—</span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── Whole week — the admin grid, read-only (scrolls sideways on a phone) ──────
function WeekView({ groups, days, grid, me, operating, colourOf, nameOf }) {
  const th = { padding: '6px 4px', fontSize: '0.62rem', fontWeight: 800, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', whiteSpace: 'nowrap' };
  return (
    <div style={S.card}>
      <h2 style={S.title}>The week</h2>
      <p style={S.sub}>Scroll sideways for the rest of the week.</p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: 620 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', minWidth: 118, position: 'sticky', left: 0, background: 'var(--hp-surface)', zIndex: 2 }}>Position</th>
              {days.map((d, i) => (
                <th key={d} style={{ ...th, minWidth: 74, opacity: isOperatingDay(d, operating) ? 1 : 0.5 }}>
                  {DOW[i]}<br /><span style={{ fontWeight: 500 }}>{new Date(d + 'T00:00').getDate()}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map(g => (
              <Fragment key={g.key}>
                <tr>
                  <td colSpan={days.length + 1} style={{ padding: 0, border: 'none' }}>
                    <div style={{ marginTop: 10, padding: '4px 10px', background: `${g.colour}14`, borderTop: `2px solid ${g.colour}`, borderLeft: `4px solid ${g.colour}`, borderTopLeftRadius: 8, borderTopRightRadius: 8, color: g.colour, fontSize: '0.64rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{g.label}</div>
                  </td>
                </tr>
                {g.slots.map(sl => (
                  <tr key={sl.Slot} style={{ borderTop: '1px solid var(--hp-border)' }}>
                    <td style={{ padding: '5px 6px 5px 10px', fontSize: '0.72rem', fontWeight: 700, borderLeft: `4px solid ${g.colour}`, background: `${g.colour}14`, position: 'sticky', left: 0, zIndex: 1, maxWidth: 140 }}>
                      {sl.Slot}
                      <div style={{ color: 'var(--hp-dim)', fontWeight: 400, fontSize: '0.64rem' }}>{sl['Default Start']}–{sl['Default End']}</div>
                    </td>
                    {days.map(d => {
                      if (!isOperatingDay(d, operating)) {
                        return <td key={d} style={{ padding: '2px 3px' }}><div style={{ minHeight: 38, borderRadius: 6, border: '1px dashed var(--hp-border)', color: 'var(--hp-dim)', fontSize: '0.64rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>—</div></td>;
                      }
                      const a = grid[d + '|' + sl.Slot];
                      const mine = a && a.login === me;
                      const col = a && a.login ? colourOf(a.login) : '';
                      return (
                        <td key={d} style={{ padding: '2px 3px' }}>
                          <div title={a && a.login ? `${a.name || a.login} · ${a.start}–${a.end}` : ''}
                            style={{
                              minHeight: 38, borderRadius: 6, padding: '4px 3px', textAlign: 'center', fontSize: '0.68rem', lineHeight: 1.25,
                              background: a && a.login ? (col ? col + '2b' : 'rgba(201,168,76,0.10)') : 'var(--hp-card, #1E1E24)',
                              border: `1px solid ${mine ? 'var(--hp-gold)' : a && a.login ? (col || 'var(--hp-border)') : 'var(--hp-border)'}`,
                              color: a && a.login ? 'var(--hp-text)' : 'var(--hp-dim)',
                            }}>
                            {a && a.login ? (<>
                              <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameOf(a.login, a.name)}</div>
                              <div style={{ color: 'var(--hp-dim)', fontSize: '0.6rem' }}>{a.start}–{a.end}</div>
                            </>) : '—'}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
